import { type Address, zeroAddress } from "viem"
import {
  arbitrum,
  avalanche,
  base,
  bsc,
  foundry,
  linea,
  mainnet,
  mantle,
  optimism,
  sepolia,
} from "viem/chains"
import {
  eacAggregatorProxyAbi,
  erc20Abi,
  fixedCyclicalBinaryIrmInfo,
  merklDistributorAbi,
  oracleTypesAbi,
  permit2Abi,
  pythAbi,
  redstoneCoreOracleAbi,
  weth9Abi,
} from "../abi"
import { brevisBatchDistributorAbi } from "../abi/brevisBatchDistributorAbi"
import { brevisDistributorAbi } from "../abi/brevisDistributorAbi"
import {
  arbitrumAddresses,
  avalancheAddresses,
  bartioAddresses,
  baseAddresses,
  berachainAddresses,
  bobAddresses,
  bscAddresses,
  hyperevmAddresses,
  inkAddresses,
  lineaAddresses,
  mainnetAddresses,
  mantleAddresses,
  monadAddresses,
  plasmaAddresses,
  sonicAddresses,
  swellAddresses,
  tacAddresses,
  unichainAddresses,
} from "../addresses"
import {
  bartio,
  berachain,
  bob,
  hyperevm,
  ink,
  monad,
  plasma,
  sonic,
  swell,
  tac,
  tenderly,
  unichain,
} from "../chains"
import { SUPPORTED_CHAIN_IDS, type SupportedChainId } from "../config"
import {
  BTC_ADDRESS,
  NATIVE_ADDRESS,
  USDCA,
  USD_TOKEN,
  WAVAX_ADDRESS,
  WBERA_ADDRESS,
  WBNB_ADDRESS,
  WETH9,
  WMNT_ADDRESS,
  WMON_ADDRESS,
  WNATIVE_ADDRESS,
  WSONIC_ADDRESS,
  WXPL_ADDRESS,
} from "../currency"

/**
 * ENTRY GENERATORS
 * =============================================
 */
const createGenericStaticEntry = <
  TInnerAbi,
  TAddress extends Record<SupportedChainId, Address>,
>(
  abi: TInnerAbi,
  address: TAddress,
) => {
  const entry: {
    address: TAddress
    abi: TInnerAbi
  } = {
    abi: abi,
    address,
  }
  return entry
}

const createGenericStaticABIEntry = <TAbi>(abi: TAbi) => {
  return { abi }
}

function createOraclePriceFeedsEntry(
  abi: typeof eacAggregatorProxyAbi,
  addresses: Record<SupportedChainId, Record<Address, Address>>,
) {
  return {
    address: addresses,
    abi: abi,
  }
}

const createAbiForAllChains = <TAbi>(abi: TAbi) =>
  Object.fromEntries(
    SUPPORTED_CHAIN_IDS.map((chainId) => [chainId, abi]),
  ) as Record<SupportedChainId, TAbi>

/**
 * ORACLE TO PRICE FEEDS
 * =============================================
 */

const MAINNET_FEEDS: Record<Address, Address> = {
  [WETH9[mainnet.id]]: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
  [NATIVE_ADDRESS]: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
  [USDCA[mainnet.id]]: "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const SEPOLIA_FEEDS: Record<Address, Address> = {
  [WETH9[sepolia.id]]: "0x694AA1769357215DE4FAC081bf1f309aDC325306",
  [NATIVE_ADDRESS]: "0x694AA1769357215DE4FAC081bf1f309aDC325306",
  [USDCA[sepolia.id]]: "0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E",
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const ARBITRUM_FEEDS: Record<Address, Address> = {
  [WETH9[arbitrum.id]]: "0x639Fe6ab55C921f74e7fac1ee960C0B6293ba612",
  [NATIVE_ADDRESS]: "0x639Fe6ab55C921f74e7fac1ee960C0B6293ba612",
  [USDCA[arbitrum.id]]: "0x50834F3163758fcC1Df9973b6e91f0F0F0434aD3",
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const BARTIO_FEEDS: Record<Address, Address> = {
  [WETH9[bartio.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[bartio.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const BASE_FEEDS: Record<Address, Address> = {
  [WETH9[base.id]]: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
  [NATIVE_ADDRESS]: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
  [USDCA[base.id]]: "0x7e860098F58bBFC8648a4311b374B1D669a2bc6B",
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const SWELL_FEEDS: Record<Address, Address> = {
  [WETH9[swell.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[swell.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress, // for mapping of all unit of accounts
} as const

const SONIC_FEEDS: Record<Address, Address> = {
  [WETH9[sonic.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WSONIC_ADDRESS,
  [USDCA[sonic.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WSONIC_ADDRESS]: zeroAddress, // WE add the SONIC Adress
} as const

const UNICHAIN_FEEDS: Record<Address, Address> = {
  [WETH9[unichain.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[unichain.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
} as const

const BERACHAIN_FEEDS: Record<Address, Address> = {
  [WETH9[berachain.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WBERA_ADDRESS,
  [USDCA[berachain.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WBERA_ADDRESS]: zeroAddress,
} as const

const INK_FEEDS: Record<Address, Address> = {
  [WETH9[ink.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[ink.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
} as const

const AVALANCHE_FEEDS: Record<Address, Address> = {
  [WETH9[avalanche.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WAVAX_ADDRESS,
  [USDCA[avalanche.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WAVAX_ADDRESS]: zeroAddress,
} as const

const BOB_FEEDS: Record<Address, Address> = {
  [WETH9[bob.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[bob.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [BTC_ADDRESS]: zeroAddress,
} as const

const BSC_FEEDS: Record<Address, Address> = {
  [WETH9[bsc.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[bsc.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WBNB_ADDRESS]: zeroAddress,
} as const

const HYPE_FEEDS: Record<Address, Address> = {
  [WETH9[hyperevm.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[hyperevm.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
} as const

const TAC_FEEDS: Record<Address, Address> = {
  [WETH9[hyperevm.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[hyperevm.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
} as const

const LINEA_FEEDS: Record<Address, Address> = {
  [WETH9[linea.id]]: zeroAddress,
  [NATIVE_ADDRESS]: zeroAddress,
  [USDCA[linea.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
} as const

const MANTLE_FEEDS: Record<Address, Address> = {
  [WETH9[mantle.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WMNT_ADDRESS,
  [USDCA[mantle.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WMNT_ADDRESS]: zeroAddress,
} as const

const PLASMA_FEEDS: Record<Address, Address> = {
  [WETH9[plasma.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WXPL_ADDRESS,
  [USDCA[plasma.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WXPL_ADDRESS]: zeroAddress,
} as const

const MONAD_FEEDS: Record<Address, Address> = {
  [WETH9[monad.id]]: zeroAddress,
  [NATIVE_ADDRESS]: WMON_ADDRESS,
  [USDCA[monad.id]]: zeroAddress,
  [USD_TOKEN.address]: zeroAddress,
  [WMON_ADDRESS]: zeroAddress,
} as const

const ORACLE_TO_PRICE_FEEDS: Record<
  SupportedChainId,
  Record<Address, Address>
> = {
  [mainnet.id]: MAINNET_FEEDS,
  [base.id]: BASE_FEEDS,
  [bartio.id]: BARTIO_FEEDS,
  [sepolia.id]: SEPOLIA_FEEDS,
  [foundry.id]: MAINNET_FEEDS,
  [tenderly.id]: MAINNET_FEEDS,
  [arbitrum.id]: ARBITRUM_FEEDS,
  [swell.id]: SWELL_FEEDS,
  [sonic.id]: SONIC_FEEDS,
  [unichain.id]: UNICHAIN_FEEDS,
  [berachain.id]: BERACHAIN_FEEDS,
  [ink.id]: INK_FEEDS,
  [avalanche.id]: AVALANCHE_FEEDS,
  [bob.id]: BOB_FEEDS,
  [bsc.id]: BSC_FEEDS,
  [hyperevm.id]: HYPE_FEEDS,
  [tac.id]: TAC_FEEDS,
  [linea.id]: LINEA_FEEDS,
  [mantle.id]: MANTLE_FEEDS,
  [plasma.id]: PLASMA_FEEDS,
  [monad.id]: MONAD_FEEDS,
} as const
/**
 *  REDSTONE ORACLE
 * =============================================
 * There is only two options for the data service id:
 * - redstone-primary-prod
 * - redstone-primary-demo
 */

export const REDSTONE_DATA_SERVICE_IDS: Record<SupportedChainId, string> = {
  [mainnet.id]: "redstone-primary-prod",
  [tenderly.id]: "redstone-primary-prod",
  [bartio.id]: "redstone-primary-prod",
  [foundry.id]: "redstone-primary-prod",
  [sepolia.id]: "redstone-primary-demo",
  [arbitrum.id]: "redstone-primary-prod",
  [base.id]: "redstone-primary-prod",
  [swell.id]: "redstone-primary-prod",
  [sonic.id]: "redstone-primary-prod",
  [berachain.id]: "redstone-primary-prod",
  [ink.id]: "redstone-primary-prod",
  [unichain.id]: "redstone-primary-prod",
  [avalanche.id]: "redstone-primary-prod",
  [bob.id]: "redstone-primary-prod",
  [bsc.id]: "redstone-primary-prod",
  [hyperevm.id]: "redstone-primary-prod",
  [tac.id]: "redstone-primary-prod",
  [linea.id]: "redstone-primary-prod",
  [mantle.id]: "redstone-primary-prod",
  [plasma.id]: "redstone-primary-prod",
  [monad.id]: "redstone-primary-prod",
}
/**
 * PYTH ORACLE
 * =============================================
 * data: https://docs.pyth.network/price-feeds/contract-addresses/evm#mainnets
 */
const PYTH_PROXY_CONTRACT_ADDRESS: Record<SupportedChainId, Address> = {
  [mainnet.id]: "0x4305FB66699C3B2702D4d05CF36551390A4c69C6",
  [sepolia.id]: "0x4305FB66699C3B2702D4d05CF36551390A4c69C6",
  [bartio.id]: zeroAddress,
  [tenderly.id]: "0x4305FB66699C3B2702D4d05CF36551390A4c69C6",
  [foundry.id]: "0x4305FB66699C3B2702D4d05CF36551390A4c69C6",
  [arbitrum.id]: "0xff1a0f4744e8582DF1aE09D5611b887B6a12925C",
  [base.id]: "0x8250f4aF4B972684F7b336503E2D6dFeDeB1487a",
  [swell.id]: "0xDd24F84d36BF92C65F92307595335bdFab5Bbd21",
  [sonic.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43",
  [berachain.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43",
  [ink.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43",
  [unichain.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43",
  [avalanche.id]: "0x4305fb66699c3b2702d4d05cf36551390a4c69c6",
  [bob.id]: zeroAddress, // Not supported for now
  [bsc.id]: "0x4D7E825f80bDf85e913E0DD2A2D54927e9dE1594",
  [hyperevm.id]: "0xe9d69CdD6Fe41e7B621B4A688C5D1a68cB5c8ADc",
  [tac.id]: zeroAddress,
  [linea.id]: "0xA2aa501b19aff244D90cc15a4Cf739D2725B5729",
  [mantle.id]: "0xA2aa501b19aff244D90cc15a4Cf739D2725B5729",
  [plasma.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43",
  [monad.id]: "0x2880aB155794e7179c9eE2e38200202908C17B43", // TODO: Add Monad feed
} as const

/**
 * PERMIT 2
 * =============================================
 */
const PERMIT_2_ABI = createAbiForAllChains(permit2Abi)
const PERMIT_2_CONTRACT_ADDRESS: Record<SupportedChainId, Address> = {
  [mainnet.id]: mainnetAddresses.coreAddresses.permit2,
  [base.id]: baseAddresses.coreAddresses.permit2,
  [sepolia.id]: mainnetAddresses.coreAddresses.permit2,
  [bartio.id]: bartioAddresses.coreAddresses.permit2,
  [tenderly.id]: mainnetAddresses.coreAddresses.permit2,
  [foundry.id]: mainnetAddresses.coreAddresses.permit2,
  [arbitrum.id]: arbitrumAddresses.coreAddresses.permit2,
  [swell.id]: swellAddresses.coreAddresses.permit2,
  [sonic.id]: sonicAddresses.coreAddresses.permit2,
  [berachain.id]: berachainAddresses.coreAddresses.permit2,
  [ink.id]: inkAddresses.coreAddresses.permit2,
  [unichain.id]: unichainAddresses.coreAddresses.permit2,
  [avalanche.id]: avalancheAddresses.coreAddresses.permit2,
  [bob.id]: bobAddresses.coreAddresses.permit2,
  [bsc.id]: bscAddresses.coreAddresses.permit2,
  [hyperevm.id]: hyperevmAddresses.coreAddresses.permit2,
  [tac.id]: tacAddresses.coreAddresses.permit2,
  [linea.id]: lineaAddresses.coreAddresses.permit2,
  [mantle.id]: mantleAddresses.coreAddresses.permit2,
  [plasma.id]: plasmaAddresses.coreAddresses.permit2,
  [monad.id]: monadAddresses.coreAddresses.permit2,
} as const

/**
 * MERKL DISTRIBUTOR
 * =============================================
 */
export const MERKl_DISTRIBUTOR_CONTRACT_ADDRESS: Record<number, Address> = {
  [mainnet.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [base.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [sepolia.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [bartio.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [tenderly.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [foundry.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [arbitrum.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [swell.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [sonic.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [unichain.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [berachain.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [ink.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [avalanche.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [bob.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [bsc.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [hyperevm.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [tac.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [optimism.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [linea.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [mantle.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [plasma.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [monad.id]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
  [137]: "0x3Ef3D8bA38EBe18DB133cEc108f4D14CE00Dd9Ae",
} as const

// ClaimAll contract addresses
// https://incentra-docs.brevis.network/developer-sdk/read-and-claim-rewards#claim-all-rewards-in-a-single-transaction
// All the addresses are zero because the address is comming from the API
const BREVIS_DISTRIBUTOR_CONTRACT_ADDRESS: Record<number, Address> = {
  [mainnet.id]: "0x39ae8501186E8F4d7b120981DDaD5db8915A6371",
  [base.id]: zeroAddress,
  [sepolia.id]: zeroAddress,
  [bartio.id]: zeroAddress,
  [tenderly.id]: zeroAddress,
  [foundry.id]: zeroAddress,
  [arbitrum.id]: "0x273d0d19eaC2861FCF6B21893AD6d71b018E25aB",
  [swell.id]: zeroAddress,
  [sonic.id]: zeroAddress,
  [unichain.id]: zeroAddress,
  [berachain.id]: zeroAddress,
  [ink.id]: zeroAddress,
  [avalanche.id]: zeroAddress,
  [bob.id]: zeroAddress,
  [bsc.id]: "0x3704597fdE1f11E4C87423B9246b295c31bfbb8d",
  [optimism.id]: zeroAddress,
  [hyperevm.id]: zeroAddress,
  [tac.id]: zeroAddress,
  [mantle.id]: zeroAddress,
  [plasma.id]: zeroAddress,
  [linea.id]: zeroAddress,
  [monad.id]: zeroAddress,
} as const

/**
 * AUXILIARY CONTRACTS
 * =============================================
 *
 * These contracts are not synced dynamically with the backend
 * and are managed only in the frontend codebase.
 *
 * The contracts in this section include:
 * - oracleToPriceFeeds: Maps oracle addresses to their price feed contracts
 * - oracleTypes: Contains oracle type definitions and interfaces
 * - redstoneCoreOracle: Redstone's core oracle implementation
 * - pythOracle: Pyth Network's oracle implementation
 * - permit2: Uniswap's permit2 contract for token approvals
 * - merklDistributor: Contract for distributing rewards via Merkle proofs
 *
 *
 * Each contract entry is created using helper functions that ensure consistent
 * formatting and validation across chains:
 * - createGenericStaticEntry: For contracts with static ABIs/addresses
 * - createOraclePriceFeedsEntry: Specifically for oracle price feed mappings
 */
const auxiliaryContracts = {
  oracleToPriceFeeds: createOraclePriceFeedsEntry(
    eacAggregatorProxyAbi,
    ORACLE_TO_PRICE_FEEDS,
  ),
  oracleTypes: createGenericStaticABIEntry(oracleTypesAbi),
  redstoneCoreOracle: createGenericStaticABIEntry(redstoneCoreOracleAbi),
  pythOracle: createGenericStaticEntry(pythAbi, PYTH_PROXY_CONTRACT_ADDRESS),
  permit2: createGenericStaticEntry(PERMIT_2_ABI, PERMIT_2_CONTRACT_ADDRESS),
  weth: createGenericStaticEntry(weth9Abi, WNATIVE_ADDRESS),
  merklDistributor: createGenericStaticEntry(
    merklDistributorAbi,
    MERKl_DISTRIBUTOR_CONTRACT_ADDRESS,
  ),
  brevisBatchDistributor: createGenericStaticEntry(
    brevisBatchDistributorAbi,
    BREVIS_DISTRIBUTOR_CONTRACT_ADDRESS,
  ),
  brevisDistributor: createGenericStaticEntry(
    brevisDistributorAbi,
    BREVIS_DISTRIBUTOR_CONTRACT_ADDRESS,
  ),
  fixedCyclicalBinaryIrmInfo: createGenericStaticABIEntry(
    fixedCyclicalBinaryIrmInfo,
  ),
  erc20: createGenericStaticABIEntry(erc20Abi),
}

export default auxiliaryContracts
